import LogInFormProvider from '@jetshop/ui/Auth/LogInFormProvider'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useFormikContext } from 'formik'
import { useApolloClient } from '@apollo/react-hooks'
import { GlobalError } from '../Forms/GlobalError'
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig'
import { useIntl } from '@jetshop/intl'
import { LogIn } from 'iconoir-react'  
import SiteButton from '../Core/SiteButton'
import { NavArrowRight } from 'iconoir-react'
import { Link } from 'react-router-dom'
import InputWithLabel from '../Forms/InputWithLabelVB'
import ExternalCustomerLookupStatusQuery from './ExternalCustomerLookupStatus.gql'
import { getExternalCustomerLoginMessage } from '../Voyado/externalCustomerLoginMessage'
import ContainerHeaderContent from './UI/AuthPagesComps/HeaderContent'
import BtnContentArrowLoading from './UI/FormComps/BtnContentArrowLoading'
import {
   PageBanner as LogInBanner,
   AuthPageHeaderTitle as LogInBannerTitle,
   AuthContentHeaderTitle as LoginContentHeaderTitle,
} from './UI/authPages'
import { FieldsWrapper, FormButtonsContainer } from './UI/Form'
import { styled } from 'linaria/react'
import { theme } from '../Theme'
import { pxToRem } from '../../utils/css'
import GamifieraAvatars from '../Gamifiera/GamifieraAvatars'

const LoginPageContainer = styled.div`
   background-color: white;
   display: flex;
   flex-direction: column-reverse;

   ${theme.above.lg} {
      display: grid;
      grid-template-columns: 49% 1fr;
      min-height: calc(100vh - 60px);
   }

   ${theme.above.xxxl} {
      grid-template-columns: 60% 1fr;
   }
`

const ImageSection = styled.div`
   position: relative;
   width: 100%;
   border-radius: 0px 40rem 40rem 0px;
   height: 70%;
   margin-y: auto;
   background-image: url('https://verktygsboden.se/pub_docs/files//Image_Login2.jpg?width=1200&method=fit&height=400&type=webp');
   background-size: cover;
   background-position: center;
   background-repeat: no-repeat;
   display: flex;
   flex-direction: column;
   justify-content: space-between;
   color: white;
   
   ${theme.below.md} {
      min-height: ${pxToRem(400)};
      padding: ${pxToRem(32)} ${pxToRem(24)};
   }

   ${theme.below.lg} {
      border-radius: 0px;
   }

   &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(25, 81, 120, 0.75);
      z-index: 1;
      border-radius: inherit;
   }

   > * {
      position: relative;
      z-index: 2;
   }
`

const ImageSectionTitle = styled.h1`
   font-family: var(--font-industry);
   font-size: ${pxToRem(40)};
   font-weight: ${theme.font.weight.bold};
   margin-bottom: ${pxToRem(16)};
   line-height: 1.2;
   
   ${theme.below.md} {
      font-size: ${pxToRem(32)};
   }
`

const ImageSectionIntro = styled.p`
   font-family: var(--font-grotesk);
   font-size: ${theme.font.size.l};
   margin-bottom: ${pxToRem(24)};
   line-height: 1.6;
   
   ${theme.below.md} {
      font-size: ${theme.font.size.m};
   }
`

const BenefitsList = styled.ul`
   list-style: none;
   padding: 0;
   margin: 0 0 ${pxToRem(32)} 0;
   font-family: var(--font-grotesk);
   li {
      font-size: ${theme.font.size.m};
      margin-bottom: 0px;
      line-height: 1.6;
      
      &::before {
         content: '•';
         margin-right: ${pxToRem(8)};
         font-size: ${pxToRem(20)};
      }
   }
`

const ButtonText = styled.span`
`

const ForgotPasswordLink = styled(Link)`
   font-size: ${theme.font.size.s};
   color: ${theme.colors.blue};
   text-decoration: underline;
   font-family: var(--font-industry);
   cursor: pointer;
   width: 100%;
   display: block;
   text-align: end;
   margin-right: 0.25rem;
   
   &:hover {
      text-decoration: none;
   }
`

const FormSection = styled.div`
   width: 100%;
   background-color: white;
   display: flex;
   flex-direction: column;
   justify-content: center;
   /* Extra right/bottom space so the login CTA clears the Kundo chat launcher */
   padding: 0 ${pxToRem(96)} ${pxToRem(88)} ${pxToRem(64)};

   ${theme.below.lg} {
      padding: ${pxToRem(64)} ${pxToRem(16)} ${pxToRem(88)};
   }

   ${theme.below.md} {
      padding: ${pxToRem(32)} ${pxToRem(16)} ${pxToRem(88)};
   }

   .login-header {
      ${theme.above.lg} {
         margin-top: 1.5rem;
      }
   }
`
const ImageSectionWrapper = styled.div`
   display: flex;
   flex-direction: column;
   justify-content: center;
`

const ImageSectionContent = styled.div`
   display: flex;
   flex-direction: column;
   justify-content: center;
   height: 100%;

   ${theme.below.lg} {
      padding-top: 2rem;
      padding-bottom: 2rem;
   }
`

const FormButtonsContainerLoginPage = styled(FormButtonsContainer)`
   margin-top: 1.0rem;
   gap: 0.75rem;
   margin-right: auto !important;
   display: flex;
   flex-wrap: wrap;
`

const loginButtonBase = `
   && {
      border-radius: 2rem;
      padding: 1rem 1.25rem;
      font-family: var(--font-industry);
      font-size: 0.975rem;
      font-weight: 700;
      line-height: 1.25;
      box-sizing: border-box;
   }
`

const LoginButton = styled(SiteButton)`
   ${loginButtonBase}
`

const SignUpLinkButton = styled(SiteButton)`
   ${loginButtonBase}
`

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Voyado has no Norce-only status; NON_EXISTING_CUSTOMER must still allow password login.
const BLOCKED_LOGIN_STATUSES = new Set([
   'ACTIVATION_REQUIRED',
   'ADDITIONAL_USER_DATA_REQUIRED',
   'COUNTRY_NOT_VALID',
])

function LoginFormContent({ globalError, isSubmitting, isValid, routes, t }) {
   const { values, submitForm, setStatus } = useFormikContext()
   const client = useApolloClient()
   const sectionRef = useRef(null)
   const [lookupMessage, setLookupMessage] = useState(null)
   const [isCheckingAccount, setIsCheckingAccount] = useState(false)

   useEffect(() => {
      setLookupMessage(null)
   }, [values.email])

   const handleLoginClick = useCallback(async () => {
      setLookupMessage(null)
      setStatus(undefined)

      const email = values.email?.trim()
      if (!email || !EMAIL_PATTERN.test(email)) {
         submitForm()
         return
      }

      setIsCheckingAccount(true)

      try {
         const { data } = await client.query({
            query: ExternalCustomerLookupStatusQuery,
            variables: { email },
            fetchPolicy: 'network-only',
         })

         const status = data?.externalCustomerLookup?.status
         if (BLOCKED_LOGIN_STATUSES.has(status)) {
            setLookupMessage(getExternalCustomerLoginMessage(status, routes))
            return
         }

         submitForm()
      } catch {
         submitForm()
      } finally {
         setIsCheckingAccount(false)
      }
   }, [client, routes, setStatus, submitForm, values.email])

   useEffect(() => {
      const form = sectionRef.current?.closest('form')
      if (!form) return

      const onSubmit = event => {
         event.preventDefault()
         event.stopPropagation()
         handleLoginClick()
      }

      form.addEventListener('submit', onSubmit, true)
      return () => form.removeEventListener('submit', onSubmit, true)
   }, [handleLoginClick])

   const isLoading = isSubmitting || isCheckingAccount

   return (
      <section ref={sectionRef}>
         <FieldsWrapper>
            <InputWithLabel
               label={t('E-mail address')}
               placeholder={t('provide your email address')}
               id={'email'}
               type={'email'}
               name={'email'}
               required={true}
               disableValidation={false}
            />
            <InputWithLabel
               label={t('Password')}
               placeholder={t('provide your password')}
               id={'password'}
               type={'password'}
               name="password"
               required={true}
               disableValidation={false}
            />
         </FieldsWrapper>
         {lookupMessage && <GlobalError>{lookupMessage}</GlobalError>}
         {!lookupMessage && globalError && (
            <GlobalError>{t('Wrong password, please try again.')}</GlobalError>
         )}
         <FormButtonsContainerLoginPage>
            <SignUpLinkButton
               styleType="hollow"
               color="blue"
               icon={true}
               href={routes.signup.path}
            >
               <ButtonText>
                  {t('Not yet a member? Sign Up')}
               </ButtonText>
               <NavArrowRight
                  strokeWidth={2.0}
                  style={{ marginLeft: '0.3rem' }}
               ></NavArrowRight>
            </SignUpLinkButton>
            <LoginButton
               type="submit"
               styleType="solid"
               color="blue"
               icon={true}
               disabled={!isValid || isLoading}
            >
               <ButtonText>
                  {t('Log in')}
               </ButtonText>
               <BtnContentArrowLoading loading={isLoading} />
            </LoginButton>
            <ForgotPasswordLink to={routes.forgotPassword.path}>
               {t('Forgot password?')}
            </ForgotPasswordLink>
         </FormButtonsContainerLoginPage>
      </section>
   )
}

function LogInPage() {
   const [loading, setLoading] = useState(true)
   const { routes } = useShopConfig()
   const t = useIntl()

   useEffect(() => {
      setLoading(false)
   }, [setLoading])

   if (loading) {
      return <div style={{ height: '972px', width: '100%' }}></div>
   }

   return (
      <div>
         <LogInFormProvider redirect="my-pages">
            {({ globalError, isSubmitting, isValid }) => (
               <>
                  <LoginPageContainer>
                     <ImageSectionWrapper>
                        <ImageSection>
                           <ImageSectionContent className='px-128'>
                              <ImageSectionTitle>{t('My Pages')}</ImageSectionTitle>
                              <ImageSectionIntro>
                                 {t('By being logged in on My Pages you get even more!')}
                              </ImageSectionIntro>
                              <BenefitsList>
                                 {/* <li>Personliga erbjudanden och rabatter</li> */}
                                 <li>{t('Order history')}</li>
                                 <li>{t('Change your information and interests')}</li>
                                 <li>{t('Saved favorites')}</li>
                                 <li>{t('Community')}</li>
                                 <li>{t('Competitions and offers')}</li>
                              </BenefitsList>
                              {t('Have you not logged in before?')}
                              <br />
                              {t('Choose "Register" so you are only a few clicks away from a new world with us at Verktygsboden.')}
                           </ImageSectionContent>
                        </ImageSection>
                     </ImageSectionWrapper>
                     <FormSection>
                        <GamifieraAvatars />
                        <ContainerHeaderContent className="login-header">
                           <LogIn
                              strokeWidth={2.0}
                              width={38}
                              height={38}
                              style={{ marginLeft: '-8px' }}
                           ></LogIn>
                           <LoginContentHeaderTitle
                              style={{
                                 marginBlockEnd: '0px',
                                 marginBlockStart: '0px',
                              }}
                           >
                              {t('Login')}
                           </LoginContentHeaderTitle>
                        </ContainerHeaderContent>
                        <LoginFormContent
                           globalError={globalError}
                           isSubmitting={isSubmitting}
                           isValid={isValid}
                           routes={routes}
                           t={t}
                        />
                     </FormSection>
                  </LoginPageContainer>
               </>
            )}
         </LogInFormProvider>
      </div>
   )
}

export default LogInPage
